import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';

console.log('[CoreAPI] Initializing with API_URL:', API_URL);

const coreApi = axios.create({
    baseURL: API_URL,
    withCredentials: true,
    headers: {
        'Content-Type': 'application/json',
    }
});

let isRefreshing = false;
let failedQueue: Array<{
    resolve: (value?: any) => void;
    reject: (reason?: any) => void;
}> = [];

const processQueue = (error: any | null) => {
    failedQueue.forEach(prom => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve();
        }
    });
    failedQueue = [];
};

// Add a request interceptor to ensure consistent headers
coreApi.interceptors.request.use(
    (config) => {
        // Log request details for debugging
        console.log('[API Debug] Sending request:', {
            method: config.method,
            url: config.url,
            headers: config.headers,
            withCredentials: config.withCredentials,
            baseURL: config.baseURL,
            fullUrl: `${config.baseURL}${config.url}`,
            cookies: document.cookie
        });
        return config;
    },
    (error) => {
        console.error('[API Debug] Request error:', error);
        return Promise.reject(error);
    }
);

// Add a response interceptor for handling auth errors
coreApi.interceptors.response.use(
    (response) => {
        console.log('[API Debug] Response received:', {
            status: response.status,
            url: response.config.url,
            headers: response.headers,
            cookies: document.cookie
        });
        return response;
    },
    async (error) => {
        console.error('[API Debug] Response error:', {
            status: error.response?.status,
            url: error.config?.url,
            error: error.response?.data,
            cookies: document.cookie
        });

        const originalRequest = error.config;

        // Don't attempt refresh for session or refresh endpoints
        if (originalRequest.url?.includes('/auth/session') || 
            originalRequest.url?.includes('/auth/refresh')) {
            return Promise.reject(error);
        }

        // If the error is 401 and we haven't retried yet
        if (error.response?.status === 401 && !originalRequest._retry) {
            if (isRefreshing) {
                return new Promise((resolve, reject) => {
                    failedQueue.push({ resolve, reject });
                })
                .then(() => {
                    return coreApi(originalRequest);
                })
                .catch(err => {
                    return Promise.reject(err);
                });
            }

            originalRequest._retry = true;
            isRefreshing = true;

            try {
                console.log('[API Debug] Attempting token refresh');
                const response = await coreApi.post('/auth/refresh');
                console.log('[API Debug] Token refresh successful, cookies:', document.cookie);
                
                processQueue(null);
                return coreApi(originalRequest);
            } catch (refreshError) {
                console.error('[API Debug] Token refresh failed:', refreshError);
                processQueue(refreshError);
                
                // Only redirect to login if we're not already trying to login
                if (!originalRequest.url?.includes('/auth/login')) {
                    window.location.href = '/login';
                }
                return Promise.reject(refreshError);
            } finally {
                isRefreshing = false;
            }
        }

        return Promise.reject(error);
    }
);

export default coreApi;